<script lang="ts" setup>
import { inject, onMounted, ref, watch } from 'vue';
import { waitFrames } from '@immersive-tech/viewer-engine';

import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { Ref } from 'vue';
import type { IManifest } from '@/types/IManifest';
import type { INotice } from '@/types/INotice';
import type { ManifestHandler } from '@/viewerExtensions';

type CSVRow = { [key: string]: string };
type CSV = CSVRow[];
type CSVValidation = { validated: boolean, text?: string, language?: string, line?: number };

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const notices = inject('notices') as Ref<INotice[]>;
const translationLanguages = inject('translationLanguages') as Ref<{ [key: string]: Nullable<string> }>;
const enableDragDropFileUpload = inject('enableDragDropFileUpload') as Ref<boolean>;
const fileInput = ref<Nullable<File>>(null);
const glbStorage = inject('glbStorage') as Ref<Map<string, string>>;
const manifestHandlerExtension = viewer.value?.extensions.get('ManifestHandler') as Extension<ManifestHandler>;

const readFile = (file: File) => {
  const fileReader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    fileReader.onload = (event) => {
      const fileContent = event.target!.result as string;

      resolve(fileContent);
    };

    fileReader.onerror = (error) => {
      reject(error);
    };

    fileReader.readAsText(file);
  });
};

const fixCSVQuotes = (str: string) => {
  return str.replace(/^"(.*)"$/s, '$1').replace(/""/g, '"');
};

const parseCSV = (csvText: string): CSV => {
  let processedCSVText = csvText;

  processedCSVText = processedCSVText.replaceAll('\r\n', '\n');

  const csvLines = processedCSVText.match(/(^.+?(?:(?:"[^"]*")[^$]*?)*$)/gms);
  const csvFields = csvLines!.map((line) => line.matchAll(/(?:^|,)((?:(?:"(?:""|[^"])*")|[^,])*)/gs));
  const csvHeaders = [...csvFields[0]].map((header) => fixCSVQuotes(header[1]));

  return csvFields
    .map((matches) =>
      Object.fromEntries(
        [...[...matches].map((match) => fixCSVQuotes(match[1])).entries()].map(([left, right]) => [
          csvHeaders[left],
          right,
        ])
      )
    )
    .slice(1);
};

const fileSelected = (payload: Event) => {
  const target = payload.target as HTMLInputElement;
  const inputFile = target.files;

  if (!inputFile) return;

  [fileInput.value] = inputFile;
};

const eventPrevent = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
};

const dropHandler = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();

  const { files } = event.dataTransfer!;
  const [ file ] = files;

  fileInput.value = file;
};

const importGLB = async (file: File, extension: string) => {
  if (!viewer.value) return;

  notices.value.push({
    text: `Importing model ${file.name}`,
    background: 'bg-blue-400',
    textColor: 'text-white',
    timeout: 5000,
  });

  const modelPath = URL.createObjectURL(file);
  const modelName = file.name;
  const modelDownloader: Promise<Nullable<string>> = viewer.value.downloadModel(modelPath, modelName, extension);

  glbStorage.value.set(modelName, modelPath);

  await viewer.value.addModelToSceneAfterDownload(modelDownloader);

  const existingPartIndex = manifest.value.parts.findIndex((part) => part.path === modelName);

  if (existingPartIndex === -1) {
    manifest.value.parts.push({
      name: file.name,
      path: file.name,
      mode: manifest.value.parts.length === 0 ? 'BASE' : 'DEFERRED_ON_DEMAND',
      invert: false,
      visible: true,
    });
  }

  notices.value.push({
    text: `Model ${file.name} imported`,
    background: 'bg-blue-400',
    textColor: 'text-white',
    timeout: 5000,
  });

  if (viewer.value.scene) {
    viewer.value.renderSemaphore += 1;
    await waitFrames(30, viewer.value.scene);
    viewer.value.renderSemaphore -= 1;
  }
};

const importManifest = async (file: File) => {
  const manifestString = await readFile(file);
  const validatedManifest = manifestHandlerExtension.createValidManifest(JSON.parse(manifestString) as IManifest);

  manifest.value = validatedManifest;

  notices.value.push({
    text: 'Manifest imported',
    background: 'bg-blue-400',
    textColor: 'text-white',
    timeout: 5000,
  });
};

const validateTranslation = (csvData: CSV): CSVValidation => {
  const translationLanguageKeys = Object.keys(translationLanguages.value);
  let validated = true;
  let output: CSVValidation = { validated: true };

  // Ensure translation language keys are present in the CSV
  for (const translationLanguageKey of translationLanguageKeys) {
    if (validated === false) continue;

    if (!(translationLanguageKey in csvData[0])) {
      validated = false;
      output = {
        validated: false,
        text: `Missing translation language key ${translationLanguageKey}`,
        language: translationLanguageKey,
        line: 1,
      };
    }
  }

  for (let index = 0; index < csvData.length; ++index) {
    const row = csvData[index];

    if (validated === false) continue;

    if (row.English === '') {
      validated = false;
      output = {
        validated: false,
        text: `Missing translation language key English on line ${index + 2}`,
        language: 'English',
        line: index + 2,
      }
    }
  }

  return output;
};

const importTranslation = async (file: File) => {
  const csv = await readFile(file);
  const csvData = parseCSV(csv);
  const translationValidation = validateTranslation(csvData);

  if (translationValidation.validated === false) {
    notices.value.push({
      text: translationValidation.text ?? 'An error occured',
      background: 'bg-red-600',
      textColor: 'text-white',
      buttonText: 'text-white',
      timeout: 5000,
    });

    return;
  }

  for (const row of csvData) {
    const phrase = row.English;
    const translationLanguageEntires = Object.entries(translationLanguages.value);

    for (const [language, shortLanguage] of translationLanguageEntires) {
      if (!shortLanguage) continue;

      manifest.value.translations[phrase] = {
        ...manifest.value.translations[phrase],
        [shortLanguage]: row[language],
      };
    }
  }

  notices.value.push({
    text: 'Translations imported',
    background: 'bg-blue-400',
    textColor: 'text-white',
    timeout: 5000,
  });
};

const fileInputWatcher = (newFileInput: Nullable<File>) => {
  if (!newFileInput) return;

  const fileExtension = '.' + newFileInput.name.split('.').pop();

  switch (fileExtension) {
    case '.glb':
      importGLB(newFileInput, fileExtension);
      break;
    case '.json':
      importManifest(newFileInput);
      break;
    case '.csv':
      importTranslation(newFileInput);
      break;
    default:
      notices.value.push({
        text: 'File type ' + fileExtension + ' not supported',
        timeout: 3000,
        background: 'bg-red-600',
      });
  }
};

const enableDragDropFileUploadWatcher = (newValue: boolean) => {
  if (newValue === true) {
    document.body.addEventListener('dragenter', eventPrevent);
    document.body.addEventListener('dragover', eventPrevent);
    document.body.addEventListener('dragleave', eventPrevent);
    document.body.addEventListener('drop', dropHandler);
  } else {
    document.body.removeEventListener('dragenter', eventPrevent);
    document.body.removeEventListener('dragover', eventPrevent);
    document.body.removeEventListener('dragleave', eventPrevent);
    document.body.removeEventListener('drop', dropHandler);
  }
};

const mountHandler = () => {
  if (enableDragDropFileUpload.value === true) {
    document.body.addEventListener('dragenter', eventPrevent);
    document.body.addEventListener('dragover', eventPrevent);
    document.body.addEventListener('dragleave', eventPrevent);
    document.body.addEventListener('drop', dropHandler);
  }
};

onMounted(mountHandler);
watch(fileInput, fileInputWatcher);
watch(enableDragDropFileUpload, enableDragDropFileUploadWatcher);
</script>

<template>
  <div class="bg-white text-center border mb-1">
    <input id="drop-input" class="hidden" type="file" accept=".glb,.json,.csv" @change="fileSelected">
    <label for="drop-input" class="block w-full h-full cursor-pointer p-1">
      <p class="text-sm">Drag files to import or click to choose a file</p>
      <p class="text-xs">Accepts manifests <span class="font-bold">(.json)</span>, models <span class="font-bold">(.glb)</span>, and translations <span class="font-bold">(.csv)</span></p>
    </label>
  </div>
</template>
